import { useState } from 'react';
import {
  Card,
  CardDescription,
  CardTitle,
} from '@/components/design-system/Card';
import { Button } from '@/components/design-system/Button';
import { getFormattedDate } from '@/utils/date/getFormattedDate';
import type { AllTestKitInfo } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { useIsTestKitSubscriptionEnabled } from '@/hooks/useIsTestKitSubscriptionEnabled';
import {
  TestPurchaseOptionSelector,
  type TestPurchaseOption,
} from '@screens/ShopPage/components/TestPurchaseOptionSelector';

const PriceCalculationDisplay = ({
  testData: product,
}: {
  testData: AllTestKitInfo;
}) => {
  const discountPrice = product?.discountAmount ?? 0;
  const discountPercentage = product?.discountPercentage ?? 0;

  const hasDiscount = discountPrice > 0 || discountPercentage > 0;

  const originalPrice = product?.current_price;
  let discountedPrice = originalPrice;

  if (hasDiscount) {
    if (discountPrice > 0) {
      discountedPrice = Math.max(0, originalPrice - discountPrice);
    } else if (discountPercentage > 0) {
      discountedPrice = originalPrice * (1 - discountPercentage / 100);
    }
  }

  const finalDiscountedPrice = Math.round(discountedPrice * 100) / 100;

  return (
    <div className="flex items-center space-x-2 mb-6 justify-center md:justify-start">
      {hasDiscount && (
        <span className="typography-h3 text-secondary-foreground line-through">
          ${originalPrice.toFixed(2)}
        </span>
      )}
      <span className="typography-h3 text-success">
        ${finalDiscountedPrice.toFixed(2)}
      </span>
    </div>
  );
};

type TestCardProps = {
  test: AllTestKitInfo;
  isProcessing: boolean;
  /** Any checkout across any card is in flight — disables every card's CTA so a second click can't silently no-op. */
  isAnyOrderProcessing: boolean;
  /** Any test in the list already has an active subscription — only one test subscription is allowed at a time. */
  hasActiveTestSubscription: boolean;
  onOrder: (test: AllTestKitInfo, selectedOption: TestPurchaseOption) => void;
};

export const TestCard = ({
  test,
  isProcessing,
  isAnyOrderProcessing,
  hasActiveTestSubscription,
  onOrder,
}: TestCardProps) => {
  const isSubscriptionFlagEnabled = useIsTestKitSubscriptionEnabled();
  const isSubscribedToThis = !!test.is_subscribed;
  // Only one active test subscription is allowed at a time — once subscribed
  // to any test, every card (including the subscribed one) falls back to the
  // pre-subscription single-CTA card; the subscribed test just gets a badge.
  const showSubscriptionUI =
    isSubscriptionFlagEnabled &&
    !!test.isSubscriptionAvailable &&
    !!test.subscription_cart_url &&
    !hasActiveTestSubscription;
  const [selectedOption, setSelectedOption] =
    useState<TestPurchaseOption>('onetime');

  const defaultLabel = test?.last_test_date ? 'Retest now' : test?.cta?.ctaText;
  const ctaLabel = isProcessing
    ? 'Processing...'
    : showSubscriptionUI &&
        selectedOption === 'subscribe' &&
        test.subscription?.buttonName
      ? test.subscription.buttonName
      : defaultLabel;

  return (
    <Card
      className="overflow-hidden mb-6"
      data-testid={`shopTests-card-${test.testId}`}
    >
      <div className="px-5 pt-6">
        {(test?.productMessage || test?.last_test_date) && (
          <div
            className="pb-2 flex flex-col-reverse md:flex-row items-center md:items-start"
            data-testid="shopTests-headerSection"
          >
            {test.productMessage && (
              <span className="typography-btn-medium text-tertiary bg-background-tertiary p-2 rounded w-fit mx-auto sm:mx-0">
                {test.productMessage}
              </span>
            )}
            {test.last_test_date && (
              <span className="typography-btn-medium text-tertiary mt-1 sm:mt-0 py-2 pl-5">
                Last tested:{' '}
                {getFormattedDate(test.last_test_date, 'MMM D, YYYY')}
              </span>
            )}
          </div>
        )}

        {isSubscriptionFlagEnabled && isSubscribedToThis && (
          <div className="pb-6 flex justify-center md:justify-start">
            <span className="typography-btn-medium !font-medium text-[#146464] bg-[#EAF6F4] p-2 rounded w-fit">
              Subscribed · Renews every 6 months
            </span>
          </div>
        )}

        <div className="flex flex-col-reverse md:flex-row justify-between">
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <CardTitle
              className="typography-h2 text-primary mb-4"
              data-testid="shopTests-title"
            >
              {test.title}
            </CardTitle>

            {!showSubscriptionUI && <PriceCalculationDisplay testData={test} />}

            {test.promo && (
              <div
                className="flex flex-nowrap max-w-fit backdrop-blur-[25px]"
                data-testid="shopTests-promo"
              >
                {test.promo.splitOne && (
                  <div
                    className={`typography-caption text-primary-foreground py-2 px-3 bg-gradient-to-r from-[#57D3BD] to-[#038F76] ${
                      test.promo.splitTwo ? 'rounded-l-full' : 'rounded-full'
                    }`}
                  >
                    {test.promo.splitOne}
                  </div>
                )}
                {test.promo.splitTwo && (
                  <div className="typography-btn-medium flex items-center text-background-success py-2 px-3 rounded-r-full bg-gradient-to-r from-[#B1EAE0] to-[#A2E6D7]">
                    {test.promo.splitTwo}
                  </div>
                )}
              </div>
            )}
          </div>

          {test.productImageUrl && (
            <div
              className="flex-shrink-0 mx-auto mt-4 md:mx-0 md:mt-0"
              data-testid="shopTests-productImage"
            >
              <img
                src={test.productImageUrl}
                alt={test.title}
                className="w-auto h-auto max-h-[165px] object-contain"
              />
            </div>
          )}
        </div>
      </div>

      <div className="px-5 py-6" data-testid="shopTests-descriptionSection">
        <CardDescription className="typography-body text-tertiary mb-6">
          {test?.description}
        </CardDescription>

        {showSubscriptionUI && (
          <TestPurchaseOptionSelector
            test={test}
            selectedOption={selectedOption}
            onSelect={setSelectedOption}
            className="mb-6"
          />
        )}

        <div className="flex gap-4 w-full">
          {test?.cta && (
            <Button
              style={{ backgroundColor: test?.cta?.ctaColor }}
              className="flex-1 h-14 rounded-3xl"
              disabled={isAnyOrderProcessing}
              onClick={() =>
                onOrder(test, showSubscriptionUI ? selectedOption : 'onetime')
              }
              data-testid={`shopTests-orderButton-${test.testId}`}
            >
              {ctaLabel}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
